import { create } from 'zustand';
import authService from '../services/auth.service';
import userSettingsService from '../services/user-settings.service';

// Default lens khi user chưa chọn favorite
const DEFAULT_LENS = 'CANSLIM';

/**
 * User Store - Quản lý state của user
 */
const useUserStore = create((set, get) => ({
  // State
  user: null,
  loading: false,
  error: null,
  favoriteLensLoading: false,

  // Actions
  setUser: (user) => set({ user, error: null }),
  
  setLoading: (loading) => set({ loading }),
  
  setError: (error) => set({ error }),
  
  clearUser: () => set({ user: null, error: null }),

  /**
   * Fetch thông tin user từ API
   * Gọi khi app mount và có token
   */
  fetchUser: async () => {
    const token = authService.getToken();
    
    if (!token) {
      set({ user: null, loading: false });
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await authService.getCurrentUserFromAPI();
      set({ user: response.payload, loading: false, error: null });
    } catch (error) {
      
      // Nếu token invalid, clear token
      if (error.response?.status === 401) {
        authService.logout();
        set({ user: null, loading: false, error: null });
      } else {
        set({ loading: false, error: error.message });
      }
    }
  },

  /**
   * Login và lưu user vào store
   */
  login: async (credentials) => {
    set({ loading: true, error: null });

    try {
      const response = await authService.login(credentials);
      set({ 
        user: response.payload.user, 
        loading: false, 
        error: null 
      });
      return response;
    } catch (error) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  /**
   * Login with Facebook và lưu user vào store
   */
  loginWithFacebook: async (accessToken) => {
    set({ loading: true, error: null });

    try {
      const response = await authService.loginWithFacebook(accessToken);
      set({
        user: response.payload.user,
        loading: false,
        error: null
      });
      return response;
    } catch (error) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  /**
   * Login with Google và lưu user vào store
   */
  loginWithGoogle: async (code, redirectUri) => {
    set({ loading: true, error: null });

    try {
      const response = await authService.loginWithGoogle(code, redirectUri);
      set({
        user: response.payload.user,
        loading: false,
        error: null
      });
      return response;
    } catch (error) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  /**
   * Register và lưu user vào store
   */
  register: async (data) => {
    set({ loading: true, error: null });

    try {
      const response = await authService.register(data);
      set({ 
        user: response.payload.user, 
        loading: false, 
        error: null 
      });
      return response;
    } catch (error) {
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  /**
   * Logout và clear user khỏi store
   */
  logout: () => {
    authService.logout();
    set({ user: null, error: null, favoriteLens: null });
  },

  /**
   * Get current subscription info từ user
   */
  getCurrentSubscription: () => {
    const { user } = get();
    if (!user) return null;
    
    return {
      type: user.type,
      type_expired_at: user.type_expired_at,
    };
  },

  /**
   * Update user info (sau khi thanh toán, update profile, etc.)
   */
  updateUser: (updates) => {
    const { user } = get();
    if (!user) return;

    set({ user: { ...user, ...updates } });
  },

  // ==================== FAVORITE LENS ====================

  /**
   * Lấy favorite lens từ user object
   * @returns {string|null} Favorite lens hoặc null
   */
  getFavoriteLens: () => {
    const { user } = get();
    return user?.favorite_lens || null;
  },

  /**
   * Cập nhật favorite lens
   * @param {string} lens - CANSLIM, Momentum, or Ichimoku
   */
  updateFavoriteLens: async (lens) => {
    set({ favoriteLensLoading: true });

    try {
      const response = await userSettingsService.updateFavoriteLens(lens);
      // Cập nhật user object với favorite_lens mới
      const { user } = get();
      if (user) {
        set({
          user: { ...user, favorite_lens: response.payload.favorite_lens },
          favoriteLensLoading: false
        });
      } else {
        set({ favoriteLensLoading: false });
      }
      return response;
    } catch (error) {
      set({ favoriteLensLoading: false });
      throw error;
    }
  },

  /**
   * Xóa favorite lens (reset về null, sẽ dùng CANSLIM mặc định)
   */
  removeFavoriteLens: async () => {
    set({ favoriteLensLoading: true });

    try {
      await userSettingsService.removeFavoriteLens();
      // Cập nhật user object với favorite_lens = null
      const { user } = get();
      if (user) {
        set({
          user: { ...user, favorite_lens: null },
          favoriteLensLoading: false
        });
      } else {
        set({ favoriteLensLoading: false });
      }
    } catch (error) {
      set({ favoriteLensLoading: false });
      throw error;
    }
  },

  /**
   * Lấy lens hiện tại (favorite hoặc default)
   * @returns {string} Lens name
   */
  getCurrentLens: () => {
    const { user } = get();
    return user?.favorite_lens || DEFAULT_LENS;
  },
}));

export default useUserStore;

